import { TextButton } from "@rosette/web_components/src/ui/TextButton.js";
import { AutoSavingForm } from "@/components/form/AutoSavingForm.js";
import React, { useEffect, useRef } from "react";
import { useListenerStore } from "@rosette/web_utils/src/useListenerStore.js";
import { APIDiceStyle, APIGamePreferences, APINameColour } from "@rosette/ts/src/business/api/api_schema.js";
import { CheckboxAndLabel } from "@rosette/web_components/src/form/input/CheckboxAndLabel.js";
import { VolumeControl } from "@rosette/web_components/src/form/input/VolumeInput.js";
import { useGamePreferences } from "@/app/(api)/api/getGamePreferences/useGamePreferences.js";
import { useOptionalUser } from "@/components/env/EnvContext.js";
import { Label } from "@rosette/web_components/src/ui/Label.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { DropdownAndLabel } from "@rosette/web_components/src/form/input/DropdownAndLabel.js";
import { Dropdown } from "@rosette/web_components/src/form/input/Dropdown.js";
import { nameColours } from "@rosette/web_components/src/user/getNameColour.js";
import { Role } from "@rosette/ts/src/business/role/Role.js";
import PremiumIcon from "@/components/icon/PremiumIcon.js";
import Link from "next/link";
import { Permission } from "@rosette/ts/src/business/role/Permission.js";
import type { AssetLoader } from "@/components/assets/AssetLoader.js";
import { useOptionalRune } from "@rosette/react_utils/src/useRune.js";
import { motion, AnimatePresence } from "framer-motion";
import { fadeInOut } from "@/utils/fadeInOut.js";
import { AudioSettings } from "@rosette/web_components/src/assets/AudioSettings.js";
import { DiceStyle } from "@rosette/ts/src/business/game/DiceStyle.js";


interface PreferencesPanelProps {
    className?: string;
    assetLoader?: AssetLoader;
}


export function PreferencesPanel({ className, assetLoader }: PreferencesPanelProps) {
    const audioSettings = useOptionalRune(assetLoader?.getAudioSettings());

    const user = useOptionalUser();
    const roles = (user ? Role.list(user) : []);
    const {
        preferences, preferencesLoading, setPreferences, setSavedPreferences,
    } = useGamePreferences(user);

    const changeListeners = useListenerStore();
    const lastPrefRef = useRef<APIGamePreferences | null>(null);
    useEffect(() => {
        if (preferencesLoading)
            return;

        if (lastPrefRef.current === null) {
            lastPrefRef.current = preferences;
        }

        if (lastPrefRef.current !== preferences) {
            lastPrefRef.current = preferences;
            const timeout = setTimeout(() => changeListeners.invoke());
            return () => clearTimeout(timeout);
        }
    }, [preferences, preferencesLoading, lastPrefRef, changeListeners]);

    const canChangeNameColour = Permission.NAME_COLOURS.hasPermission(roles);

    return (
        <AutoSavingForm
            className={cn("pt-0 text-xl", className ?? null)}
            action="/api/setGamePreferences"
            registerChangeListener={listener => changeListeners.add(listener)}
            acceptUpdateJSON={json => setSavedPreferences(APIGamePreferences.parse(json))}
            validationError={null}>

            <div className="mt-0 mb-4 w-full">
                <Label
                    className={cn("mb-2", canChangeNameColour ? null : "text-subtle")}
                    htmlFor="name_colour">

                    Name Colour
                    {!canChangeNameColour && (
                        <PremiumIcon className="ml-2 text-base" />
                    )}
                </Label>

                {!canChangeNameColour && (
                    <p className="mt-0 mb-2 text-sm text-subtle">
                        Premium only.
                        {" "}
                        <Link href="/premium">
                            Unlock name colours with Gold.
                        </Link>
                    </p>
                )}

                <Dropdown
                    className="w-auto min-w-[8em]"
                    id="name_colour"
                    name="name_colour"
                    value={preferences.nameColour}
                    setValue={nameColour => setPreferences({
                        ...preferences,
                        nameColour: APINameColour.parse(nameColour),
                    })}
                    options={nameColours.map(col => ({
                        value: col.id,
                        label: col.name,
                    }))}
                    disabled={!canChangeNameColour} />
            </div>

            <VolumeControl
                id="sound_volume"
                name="sound_volume"
                volume={preferences.soundVolume}
                setVolume={volume => setPreferences({
                    ...preferences,
                    soundVolume: volume,
                })}>

                Sound Volume
            </VolumeControl>

            <VolumeControl
                id="music_volume"
                name="music_volume"
                volume={preferences.musicVolume}
                setVolume={volume => setPreferences({
                    ...preferences,
                    musicVolume: volume,
                })}>

                Music Volume
            </VolumeControl>

            <CheckboxAndLabel
                id="hide_music_controls"
                name="hide_music_controls"
                checked={preferences.hideMusicControls}
                setChecked={checked => setPreferences({
                    ...preferences,
                    hideMusicControls: checked,
                })}>

                Hide Music Controls
            </CheckboxAndLabel>

            <AnimatePresence>
                {preferences.musicVolume > 0 && audioSettings && audioSettings.musicDisabled && (
                    <motion.div key="disable_music_permanently" {...fadeInOut()}>
                        <p>
                            <span>
                                You disabled music temporarily.
                                {" "}
                                Set music volume to zero to disable permanently, or
                                {" "}
                            </span>
                            <TextButton
                                onClick={() => {
                                    if (!assetLoader)
                                        return;

                                    assetLoader.getAudioSettings().set(new AudioSettings(
                                        audioSettings.soundVolume,
                                        audioSettings.musicVolume,
                                        false,
                                    ));
                                }}>

                                click to remove the block on music.
                            </TextButton>
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            <DropdownAndLabel
                id="dice_style"
                name="dice_style"
                value={preferences.diceStyle}
                setValue={nameColour => setPreferences({
                    ...preferences,
                    diceStyle: APIDiceStyle.parse(nameColour),
                })}
                options={DiceStyle.values().map(style => ({
                    value: style.getID(),
                    label: style.getName(),
                }))}
                tooltip={styleID => DiceStyle.getByIDOrNull(styleID)?.getDesc()}>

                Dice Animation Style
            </DropdownAndLabel>

            <CheckboxAndLabel
                id="auto_dice_rolls"
                name="auto_dice_rolls"
                checked={preferences.autoDiceRolls}
                setChecked={checked => setPreferences({
                    ...preferences,
                    autoDiceRolls: checked,
                })}>

                Roll Dice Automatically
            </CheckboxAndLabel>

            <CheckboxAndLabel
                id="show_move_guidelines"
                name="show_move_guidelines"
                checked={preferences.showMoveGuidelines}
                setChecked={checked => setPreferences({
                    ...preferences,
                    showMoveGuidelines: checked,
                })}>

                Show Move Guidelines
            </CheckboxAndLabel>

            <CheckboxAndLabel
                id="auto_select_single_move_option"
                name="auto_select_single_move_option"
                checked={preferences.autoSelectSingleMoveOption}
                setChecked={checked => setPreferences({
                    ...preferences,
                    autoSelectSingleMoveOption: checked,
                })}>

                Auto-select one available move
            </CheckboxAndLabel>

            <CheckboxAndLabel
                id="disabled_reactions"
                name="disabled_reactions"
                checked={preferences.disabledReactions}
                setChecked={checked => setPreferences({
                    ...preferences,
                    disabledReactions: checked,
                })}>

                Disable reactions
            </CheckboxAndLabel>
        </AutoSavingForm>
    );
}
